var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Schema, type } from '@colyseus/schema';
import { IDLE_ACTION } from './actions.js';
// ---------------------------------------------------------------------------
// The wire's vocabulary.
//
// Three lists of ids and a band count, declared before the state that encodes
// them because a field's default is one of them. None of them is a table: what
// an archetype ranges at, what a champion tier multiplies and what an affix
// does are all `@d2/core`'s, and none of it crosses this file.
// ---------------------------------------------------------------------------
/**
 * The three archetypes `docs/GAME-DESIGN.md` names: *"melee closes to contact,
 * ranged keeps distance and shoots, caster uses elemental damage."*
 *
 * Declared here rather than imported from `@d2/core` because this is the *wire's*
 * vocabulary: a code that crosses the network has to keep meaning what it meant
 * last release, and core's table order is core's business. It is the narrow
 * structural interface `CLAUDE.md` asks two parallel owners to agree on, and it
 * is the whole of what this package knows about a monster's behaviour — the
 * ranges, the reach and the aggro radius stay in core, where the rules live.
 */
export const MONSTER_ARCHETYPES = Object.freeze(['melee', 'ranged', 'caster']);
/** The archetype code of a monster nobody described. Never valid; see `MonsterState.archetype`. */
export const NO_ARCHETYPE = 0;
/**
 * The five D2 champion types, in the order their codes are assigned.
 *
 * Ids only. What a tier multiplies, and by how much per difficulty, is core's
 * table (`elites.ts`) and none of it is here. **Append only**: a code is a wire
 * value, and reordering this list re-labels every champion already deployed.
 */
export const CHAMPION_TIER_IDS = Object.freeze([
    'champion',
    'berserker',
    'fanatic',
    'ghostly',
    'possessed',
]);
/** The champion code of a monster that is not one — and the value the field already holds. */
export const NOT_A_CHAMPION = 0;
/**
 * The elite affixes, in the order their bits are assigned.
 *
 * Ids only, for the same reason as the tiers: `MonsterState.affixes` carries
 * bit *i* of this list, and the client turns a bit back into an id and asks
 * core what it means. **Append only**, and never longer than thirty-two.
 */
export const ELITE_AFFIX_IDS = Object.freeze([
    'extra-fast',
    'extra-strong',
    'cursed',
    'aura-enchanted',
    'lightning-enchanted',
    'cold-enchanted',
    'fire-enchanted',
    'magic-resistant',
    'stone-skin',
    'multiple-shots',
    'teleportation',
    'spectral-hit',
    'mana-burn',
]);
/** The affix mask of a monster carrying none — and the value the field already holds. */
export const NO_AFFIXES = 0;
/**
 * How many steps of life a monster's bar has.
 *
 * Sixteen: see `MonsterState.lifeBand` for why the number is a *write
 * frequency* decision and not a precision one.
 */
export const LIFE_BANDS = 16;
/**
 * The floor's population, twenty times a second.
 *
 * `docs/GAME-DESIGN.md`: *"Monsters are server-spawned at runtime, not derived
 * from the floor seed. Placement and pacing stay the server's to change, and
 * monsters therefore go on the wire — unlike the floor itself, which is ~40
 * bytes of seed."* That decision is what this file costs out. A seed cannot
 * describe something the server invents at runtime, so every monster is a real
 * entry in a real map, and monsters are the first thing in this game whose
 * count is not fixed by a rule somewhere: a party is four, a floor is one, a
 * pack is however many the server felt like spawning.
 *
 * ## The number this file is sized against
 *
 * Sixty. A 128×128 cave is 16,384 cells with about two thirds of them walkable,
 * so sixty bodies is roughly one per a hundred and seventy-five cells of floor.
 *
 * The bound that makes it a *budget* rather than a guess is measured in
 * `bandwidth.test.ts`, and **which** bound that is changed on 2026-08-12 when
 * the house floor went from 64×64 to 128×128. It used to be the join snapshot:
 * the population, the party and the level together had to fit inside the tile
 * grid shipping a seed saved, which was four kilobytes. That grid is the floor's
 * own area, so it is sixteen kilobytes now, and sixty — 2301 bytes with the
 * party and the floor beside it — spends an eighth of it. That ceiling stopped
 * binding anything.
 *
 * What binds sixty today is the **per-tick chase stream**, which does not move
 * with the map: twelve bytes a monster a tick, 725 for a floor all chasing at
 * once against a budget of 768, and 1445 for a hundred and twenty. Everything
 * below was still chosen at sixty rather than at one, because a field that
 * costs two bytes is a rounding error on a player and two and a half kilobytes
 * a second on a floor.
 *
 * ## Position every tick — re-derived here, not inherited from the player
 *
 * `state.ts` argues a player's position onto the wire from an interpolating
 * client and from the pathfinder otherwise crossing it. Neither argument
 * transfers unchanged — a monster is server-driven, nobody predicts it, and
 * there are fifteen times as many — so it was re-derived, and it lands the same
 * way, harder:
 *
 *   - **The cheap scheme is far worse here than it was there.** For a player,
 *     "send the destination and re-run `findPath`" needed A*, string pulling and
 *     clearance to agree bit for bit across two builds. A monster has no
 *     destination to send: it follows a flow field, re-targets every tick,
 *     separates against every other body in its pack and has its step cut short
 *     by any player in the way (`ARCHITECTURE.md`; `world.ts`). Re-deriving that
 *     client-side means running the AI, the separation pass and the blocking
 *     rule against the server's exact float64 state — the entire simulation on
 *     the wire, not merely the pathfinder.
 *   - **The channel it would replace is not cheaper anyway.** A chaser's target
 *     moves, so a destination field would be rewritten *every tick* rather than
 *     once per click: measured at 1085 bytes a tick for sixty chasers against
 *     725 for the positions alone. The cheap scheme costs half again as much as
 *     the thing it was supposed to save.
 *   - **A client has no other way to know where a monster is.** A player at
 *     least has its own input; nobody is holding a monster's.
 *
 * So: two `float32`s, twelve bytes a tick with the map-entry header, the same
 * as a player because it is the same two fields. A floor of sixty all chasing
 * at once — which is what an aggro radius with **no leash** produces the moment
 * somebody runs — is 725 bytes a tick, 14.5 kB/s. A monster that did not move
 * costs nothing, because a patch is a delta.
 *
 * What was *not* inherited is everything else the player carries. A monster has
 * no `destinationX/Y` (10 bytes a tick, above), no `lastSequence` (there is no
 * intent to acknowledge — nobody clicked), no `name`, and no `id`:
 * `PlayerState.id` duplicates its own map key and `TODO.md` #113 already calls
 * that a tidiness question at four players. At sixty it is 258 bytes of every
 * join for a value the client is already holding, so the map key is the id and
 * there is no field. What an id *is* stays the room's to choose, and its length
 * is now a bandwidth question (`TODO.md` #264).
 *
 * ## What else is deliberately absent
 *
 *   - **Facing.** Two consecutive positions give a heading, exactly as for a
 *     player, and an idle monster keeps the last one it had.
 *   - **Speed.** `DEFAULT_MOVE_SPEED`, from `@d2/core`, until it is a stat.
 *     Extra Fast is an *affix*, and the affix is on the wire (below), so a
 *     client that needs the number looks it up rather than being told it.
 *   - **The target.** Which player a monster is chasing is an answer, and
 *     answers are the server's. Nothing renders it.
 *   - **Anything the brain knows** — aggro state, cooldowns, the flow field.
 *     None of it is drawn, and all of it is core's.
 *   - **The exact life.** See below; a bar is not a damage log.
 *   - **The affix table.** See below; a reference is not a table.
 *
 * ## Field order is the wire format
 *
 * `@colyseus/schema` numbers fields by declaration order and sends the number,
 * not the name. **Append only** — the same rule, and the same reason, as
 * `PlayerState`.
 */
export class MonsterState extends Schema {
    constructor() {
        super(...arguments);
        /**
         * Continuous position in **cell coordinates**, `float32`, written every tick.
         *
         * The one per-tick field, and therefore the only one whose cost multiplies by
         * both the population *and* the tick rate. `float32` for the same reason a
         * player's is: the simulation runs in float64 and halving the field halves
         * the stream. There is no soft-correction threshold to stay under here —
         * nobody predicts a monster — so the quantisation is purely a rendering
         * input, and `Math.fround` of a cell coordinate is far below a pixel.
         *
         * Two further halvings were measured and rejected: a `uint16` fixed-point
         * pair at 1/64 of a cell is 485 bytes a tick against 725 (-33%), and packing
         * both axes into one `uint32` is 425 (-41%). Both buy their saving with a
         * hard ceiling on map size that `LevelState.width` does not otherwise have —
         * 1024 cells for the first, 256 for the second — and a second decode path on
         * a client that already has one for players. At 14.5 kB/s for the worst floor
         * this game can produce, that is a lever to pull when a floor is bigger or a
         * room is fuller, not now (`TODO.md` #262).
         */
        this.x = 0;
        this.y = 0;
        /**
         * Whether this monster is part-way along a step.
         *
         * The idle/walk animation split, and the same "not derivable in time to be
         * useful" argument `PlayerState.walking` makes: comparing two positions is a
         * tick late and a client that has just joined has one sample. It is written
         * when a monster starts or stops rather than per tick, so it costs two bytes
         * per transition and nothing in the stream.
         *
         * It does **not** say a monster is attacking. `action` does, since
         * `TODO.md` #260 was answered — and this flag stayed exactly as it was,
         * because it is the mechanical fact an interpolator reads and the action is
         * the *visual* reading of the same truth (`actions.ts`).
         */
        this.walking = false;
        /**
         * Which of the three archetypes this is, as a code (see `MONSTER_ARCHETYPES`).
         *
         * On the wire because a client has to pick a sprite and this is the only
         * classification the design gives it: `docs/GAME-DESIGN.md` names melee,
         * ranged and caster, and monster tables do not exist yet (`TODO.md` #30).
         *
         * `uint8`, one-based, with zero reserved for "nobody wrote this". A closed
         * three-member vocabulary would fit in two bits, but the field costs two
         * bytes either way and the spare codes are what let a fourth archetype be
         * appended without a wire break. Reserving zero is deliberate: a schema field
         * starts at zero, so a monster that reached a client with its archetype unset
         * would otherwise render as a melee one, silently and forever.
         */
        this.archetype = NO_ARCHETYPE;
        /**
         * Life as one of `LIFE_BANDS` bands, full at `LIFE_BANDS` and empty at zero.
         *
         * A health bar does not need 32 bits — and, more to the point, it does not
         * need a write per tick. The width of the field is not what this decision is
         * about; the *frequency* is. Poison is real damage on the fixed step
         * (`docs/GAME-DESIGN.md`, core's `effects.ts`), so an exact life is one write
         * per poisoned monster per tick: measured at 305 bytes a tick for a floor
         * under a single nova — six kilobytes a second, another 42% on top of
         * everything that floor costs to *walk*, for a number no client displays. A
         * band is written at most `LIFE_BANDS` times over a monster's entire life,
         * however long the dying takes: 19 bytes a tick for the same nova, sixteen
         * times cheaper, and 3.8 kB to drain the whole floor from full to dead once.
         *
         * That first figure used to be a quotation, because nothing on this wire
         * carried an exact life to measure. `PlayerState` does since M5 — a player's
         * life is a `uint16` point count and *not* banded, for reasons that all turn
         * on there being four of them — so `bandwidth.test.ts` now drains sixty
         * exact-life bodies, sixty banded ones and a party of four in one test: 301
         * bytes a tick, 19, and 20. The population was always the argument; it is now
         * the measurement as well.
         *
         * Sixteen bands is 6% of a bar. The client draws a bar and never a number:
         * this is the "nearly dead" signal that decides whether to keep swinging or
         * run, not a damage log. What it costs is that a hit smaller than a
         * sixteenth of a monster's life moves nothing on screen (`TODO.md` #261).
         *
         * Written only by `setMonsterLife`, which rounds a survivor *up*, so an empty
         * bar means dead and nothing else. Full by construction, which is also why a
         * spawning monster pays nothing to say so.
         */
        this.lifeBand = LIFE_BANDS;
        /**
         * The champion tier this monster carries, as a code, or `NOT_A_CHAMPION`.
         *
         * `docs/GAME-DESIGN.md`: *"Champions are tougher variants with a stat
         * multiplier."* A client that cannot tell a champion from the body beside it
         * cannot warn the player that this one hits for triple, so the identity has
         * to cross — but what the tier *does* is a table in `@d2/core`, and this
         * package neither holds tables nor restates rules. So it sends a reference
         * into `CHAMPION_TIER_IDS` and the client looks the rest up.
         *
         * Zero is "not a champion", which is what an ordinary monster's untouched
         * field already reads as: the common case costs nothing.
         */
        this.champion = NOT_A_CHAMPION;
        /**
         * The unique affixes this monster carries, as a bitmask over `ELITE_AFFIX_IDS`.
         *
         * *"Unique leaders carry affix modifiers (Extra Fast, Cursed, Aura Enchanted)
         * and bring minions. This is the loot-chase driver"* — and a loot-chase driver
         * a player cannot pick out of a crowd is not one. Affixes reach the client for
         * the same reason the champion tier does, and as the same kind of thing: a
         * reference, not a table.
         *
         * A mask rather than a list because a monster's affixes are a *set* — core
         * rolls no duplicates and one per exclusion group — and because the whole set
         * is five bytes: the same three affixes as an `ArraySchema` of id strings
         * turn a 37-byte spawn into a 93-byte one, measured. Bit *i* is
         * `ELITE_AFFIX_IDS[i]`, which is why that list is append-only: the wire's bit
         * order is a contract, and core's table order is not.
         *
         * `uint32`, so the list may grow to thirty-two ids before the field has to.
         */
        this.affixes = NO_AFFIXES;
        /**
         * Whether this monster is the boss of its pack rather than one of its minions.
         *
         * The one thing the affix mask cannot say. A unique leader and the minions it
         * brought carry the *same* affixes — that is what makes them a pack — so
         * without this a client draws five identical monsters and the player kills
         * them in whatever order, having never found the one the pack is named after.
         *
         * False by construction and meaningless without affixes, so every ordinary
         * monster and every champion pays nothing for it.
         */
        this.leader = false;
        /**
         * What this body is **doing** — appended, because field order is the wire
         * format.
         *
         * `TODO.md` #260, answered: *"A melee monster standing at its reach and
         * swinging is replicated as a monster standing still, which is the one frame a
         * player most needs to see coming."* The vocabulary, the derivation of every
         * code and both agreements with `walking` are `actions.ts`'s; what belongs
         * here is why sixty of them can afford it, because that is the question this
         * file exists to ask of every field.
         *
         * **It is written every tick and paid for on transitions**, and the two
         * expensive states are the cheap ones. A floor of sixty all chasing at once is
         * the worst per-tick stream this game can produce (725 bytes,
         * `bandwidth.test.ts`) and it is exactly the tick on which every one of those
         * sixty is `walking` and *unchanged* — the action costs nothing at all while
         * the position costs everything. A monster that arrives and starts swinging
         * pays two bytes once and then goes on striking for free, because a swing is
         * an animation many ticks long (`attackCooldownTicks`) rather than an event
         * per tick.
         *
         * What it *can* cost is bounded by the **cadence** and not by the population:
         * the field moves when a swing starts, when a blow lands on this body, and
         * when the animation ends, and no body can be struck more often than something
         * can swing at it. Sixty in melee with a party hitting back measures at a
         * handful of bytes a tick against the 768 the chase already spends, which is
         * what let this be a field on a monster rather than a field on a player.
         *
         * `uint8` and idle-at-zero for the reasons `PlayerState.action` states.
         */
        this.action = IDLE_ACTION;
    }
}
__decorate([
    type('float32'),
    __metadata("design:type", Object)
], MonsterState.prototype, "x", void 0);
__decorate([
    type('float32'),
    __metadata("design:type", Object)
], MonsterState.prototype, "y", void 0);
__decorate([
    type('boolean'),
    __metadata("design:type", Object)
], MonsterState.prototype, "walking", void 0);
__decorate([
    type('uint8'),
    __metadata("design:type", Object)
], MonsterState.prototype, "archetype", void 0);
__decorate([
    type('uint8'),
    __metadata("design:type", Object)
], MonsterState.prototype, "lifeBand", void 0);
__decorate([
    type('uint8'),
    __metadata("design:type", Object)
], MonsterState.prototype, "champion", void 0);
__decorate([
    type('uint32'),
    __metadata("design:type", Object)
], MonsterState.prototype, "affixes", void 0);
__decorate([
    type('boolean'),
    __metadata("design:type", Object)
], MonsterState.prototype, "leader", void 0);
__decorate([
    type('uint8'),
    __metadata("design:type", Object)
], MonsterState.prototype, "action", void 0);
/** The code for an archetype. Throws on one the wire cannot name — see `createMonsterState`. */
const archetypeCode = (archetype) => {
    const index = MONSTER_ARCHETYPES.indexOf(archetype);
    if (index < 0)
        throw new RangeError(`the wire cannot name the monster archetype "${archetype}"`);
    return index + 1;
};
/** The code for a champion tier id. */
const championCode = (id) => {
    const index = CHAMPION_TIER_IDS.indexOf(id);
    if (index < 0)
        throw new RangeError(`the wire cannot name the champion tier "${id}"`);
    return index + 1;
};
/** The mask for a set of elite affix ids. */
const affixMask = (ids) => {
    let mask = 0;
    for (const id of ids) {
        const bit = ELITE_AFFIX_IDS.indexOf(id);
        if (bit < 0)
            throw new RangeError(`the wire cannot name the elite affix "${id}"`);
        mask |= 1 << bit;
    }
    return mask >>> 0;
};
/**
 * Builds a `MonsterState`.
 *
 * Construction goes through a function for the reasons `createPlayerState`
 * exists — nothing depends on field order, and a monster is fully described the
 * moment it enters the map — plus one this package did not have before: it is
 * the single place where an *identity* becomes a wire code, so an id neither
 * side can name fails here, on the server that invented it, rather than
 * arriving as a monster nobody can draw.
 *
 * Life is not written. A monster is born whole, which is already what the field
 * says, and a value equal to the one a field holds is not a change — so a
 * spawning monster is charged for its position and its archetype and nothing
 * else. Everything about it after that is `setMonsterLife` and the room's own
 * assignments to `x`, `y` and `walking`.
 *
 * Which *combinations* are legal — a champion carries no affixes, a unique
 * carries no tier — is `elites.ts`'s rule, and it is deliberately not checked
 * here. This package describes what crosses the wire; a second copy of that
 * rule living in the format is exactly the coupling `ARCHITECTURE.md` keeps out.
 */
export const createMonsterState = ({ archetype, position, champion = null, affixes = [], leader = false, }) => {
    const monster = new MonsterState();
    monster.x = position.x;
    monster.y = position.y;
    monster.archetype = archetypeCode(archetype);
    monster.champion = champion === null ? NOT_A_CHAMPION : championCode(champion);
    monster.affixes = affixMask(affixes);
    monster.leader = leader;
    return monster;
};
/**
 * Records how much life a monster has left, as a band.
 *
 * The single writer of `lifeBand`, and the place the quantisation happens, so
 * no caller has to know how wide a band is or which way it rounds. Both of
 * those are decided once, here:
 *
 *   - **A survivor rounds up.** Any life at all is at least one band, so an
 *     empty bar means dead and nothing else. Rounding down would draw a corpse
 *     over a monster with 3% left, which is precisely the moment a player is
 *     deciding whether to run.
 *   - **A hit that does not cross a band writes nothing**, because the value
 *     does not change and an unchanged field is not in the patch. That is the
 *     saving, and `bandwidth.test.ts` measures it against a whole floor dying.
 *
 * A life this wire cannot describe — a maximum of zero, a negative pool, a
 * current above the maximum, a NaN — is malformed construction rather than an
 * ordinary outcome, so it throws (`CLAUDE.md`). It is the server's own number
 * and the server is the one holding it wrong.
 */
export const setMonsterLife = (monster, current, maximum) => {
    if (!(maximum > 0) || !(current >= 0) || !(current <= maximum)) {
        throw new RangeError(`a life of ${current}/${maximum} is not one this wire can band`);
    }
    monster.lifeBand = current === 0 ? 0 : Math.max(1, Math.ceil((current / maximum) * LIFE_BANDS));
};
/**
 * The archetype a code names.
 *
 * The client's half of the contract, and it refuses an unknown code rather than
 * guessing: a monster with no archetype is a monster the server failed to
 * describe, and drawing it as a melee one would hide that forever.
 */
export const monsterArchetypeOf = (code) => {
    const archetype = MONSTER_ARCHETYPES[code - 1];
    if (archetype === undefined)
        throw new RangeError(`no monster archetype has the code ${code}`);
    return archetype;
};
/**
 * The champion tier a code names, or null when the monster is not a champion.
 *
 * Null rather than a throw for zero, because "not a champion" is the ordinary
 * answer for almost every monster on the floor and not a failure at all.
 */
export const championTierIdOf = (code) => {
    if (code === NOT_A_CHAMPION)
        return null;
    const id = CHAMPION_TIER_IDS[code - 1];
    if (id === undefined)
        throw new RangeError(`no champion tier has the code ${code}`);
    return id;
};
/**
 * The affix ids a mask names, in the wire's order.
 *
 * A bit no id claims is ignored rather than refused. The list is append-only,
 * so an unknown high bit means the server has grown an affix this client has
 * never heard of — and blanking the twelve it *can* draw, or refusing the
 * monster outright, would turn a compatible change into an outage. This is the
 * same promise field order makes: a newer server stays readable by an older
 * client, minus what is new.
 */
export const eliteAffixIdsOf = (mask) => ELITE_AFFIX_IDS.filter((_id, bit) => ((mask >>> bit) & 1) === 1);
/**
 * The fraction of its life a monster has left, in `0..1`.
 *
 * What the bar is actually drawn from, so no client divides by `LIFE_BANDS`
 * itself and none has to be told which way the band rounded.
 */
export const lifeFraction = (monster) => monster.lifeBand / LIFE_BANDS;
//# sourceMappingURL=monsters.js.map